import { MapPin, Star, Utensils } from 'lucide-react';
import { ResizedImage } from '@ah/design/mobile';
import type { Venue } from '@ah/venues/shared';

const TIER_LABEL: Record<Venue['qualityTier'], string> = {
  unrated: '',
  excluded: '',
  casual: 'Casual',
  high_end: 'High-end',
  flagship: 'Flagship',
};

/**
 * Map Google Places types to a venue-format label as a fallback when the
 * LLM hasn't given us a cuisine string yet. Order matters — pick the most
 * specific tag first.
 */
const TYPE_LABEL: Array<[string, string]> = [
  ['bakery', 'Bakery'],
  ['night_club', 'Bar'],
  ['bar', 'Bar'],
  ['cafe', 'Cafe'],
  ['meal_takeaway', 'Takeaway'],
  ['restaurant', 'Restaurant'],
  ['food', 'Food'],
];

function fallbackLabel(types: string[] | undefined): string {
  if (!types) return 'Venue';
  for (const [tag, label] of TYPE_LABEL) {
    if (types.includes(tag)) return label;
  }
  return 'Venue';
}

export function venueCardTitle(_venue: Venue): string {
  return 'Would you like to attend an Underlings gathering at this venue?';
}

export function VenueCard({ venue }: { venue: Venue }) {
  const tierLabel = TIER_LABEL[venue.qualityTier];
  const formatLabel = venue.cuisine?.trim() || fallbackLabel(venue.types);
  const priceLabel = venue.priceLevel != null ? '$'.repeat(Math.max(1, venue.priceLevel)) : null;
  const body = venue.pitch?.trim() || venue.description?.trim() || null;
  const mapUrl =
    venue.lat != null && venue.lng != null
      ? `https://www.google.com/maps?q=${venue.lat},${venue.lng}&z=15&output=embed`
      : null;

  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
      {venue.photoUrl ? (
        <div className="w-full aspect-video bg-muted overflow-hidden">
          <ResizedImage src={venue.photoUrl} renderWidth={384} fallback={false} alt="" className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className="w-full aspect-video bg-amber-500 flex items-center justify-center">
          <Utensils size={40} className="text-white/70" />
        </div>
      )}

      <div className="p-6 space-y-5">
        <div>
          <span className="text-[13px] uppercase tracking-wider font-semibold text-muted-foreground">
            {formatLabel}
            {tierLabel && ` · ${tierLabel}`}
            {priceLabel && ` · ${priceLabel}`}
          </span>
          <p className="font-bold text-xl leading-tight mt-1">{venue.name}</p>
          {venue.formattedAddress && (
            <p className="flex items-start gap-1 text-[13px] text-muted-foreground mt-1">
              <MapPin size={13} className="mt-0.5 shrink-0" />
              <span>{venue.formattedAddress}</span>
            </p>
          )}
          {venue.rating != null && (
            <p className="flex items-center gap-1 text-[13px] text-muted-foreground mt-1">
              <Star size={13} className="fill-current text-amber-500" />
              {venue.rating.toFixed(1)}
              {venue.userRatingCount != null && ` · ${venue.userRatingCount.toLocaleString()} reviews`}
            </p>
          )}
        </div>

        {body && (
          <p className="text-[15px] text-foreground/85 leading-relaxed">{body}</p>
        )}

        {mapUrl && (
          <div className="rounded-xl overflow-hidden border border-border">
            <iframe
              src={mapUrl}
              title={`Map of ${venue.name}`}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-48 block"
              style={{ border: 0 }}
            />
          </div>
        )}

        {venue.weekdayHours.length > 0 && (
          <ul className="text-[13px] text-muted-foreground space-y-0.5">
            {venue.weekdayHours.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        )}

        {venue.photoAttributions.length > 0 && (
          <p className="text-[10px] text-muted-foreground/80 leading-snug">
            Photo:{' '}
            <span
              dangerouslySetInnerHTML={{ __html: venue.photoAttributions.join(' · ') }}
            />
          </p>
        )}
      </div>
    </div>
  );
}
